/**
 * +field: the Auto toggle (round button at the right end of the skill bar, key Z) and the pulsing
 * "AUTO · Hunting" badge above the cluster while it runs. Clicking calls fieldView.actions.toggleAuto
 * (the same path as the key).
 */
import type { HudWidget } from '../widgets';
import { el, setText } from '../dom';
import { fieldView } from '../../world/fieldView';
import { AUTO_STATUS_TEXT } from '../../world/autoMode';

export class AutoToggle implements HudWidget {
  readonly root: HTMLElement;
  private readonly btn: HTMLButtonElement;
  private readonly badge: HTMLElement;
  private readonly status: HTMLElement;
  /** The main quest's Auto tutorial ("Press Z…"): a coach bubble over the button while it waits. */
  private readonly coach: HTMLElement;
  private hint: boolean | null = null;
  private version = -1;
  private active: boolean | null = null;

  constructor(host: HTMLElement) {
    this.root = el('div', 'fx-auto', host);
    this.btn = el('button', 'fx-auto-btn hk-hit', this.root);
    this.btn.type = 'button';
    el('span', 'fx-auto-glyph', this.btn, 'AUTO');
    el('span', 'fx-auto-key', this.btn, 'Z');
    this.btn.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      fieldView.actions?.toggleAuto();
    });
    this.badge = el('div', 'fx-auto-badge', this.root);
    el('span', 'fx-auto-badge-tag', this.badge, 'AUTO');
    this.status = el('span', 'fx-auto-badge-status', this.badge);
    this.coach = el('div', 'fx-auto-coach hk-hidden', this.root);
    el('div', 'fx-auto-coach-title', this.coach, 'Feeling tired of swinging?');
    const line = el('div', 'fx-auto-coach-text', this.coach);
    line.append('Press ');
    el('b', 'fx-auto-coach-key', line, 'Z');
    line.append(' — your hero will fight on their own. Sit back and watch.');
  }

  update(): void {
    const v = fieldView;
    if (v.active !== this.active) {
      this.active = v.active;
      this.root.classList.toggle('hk-hidden', !v.active);
    }
    const hint = v.active && v.autoHint && !v.auto.on;
    if (hint !== this.hint) {
      this.hint = hint;
      this.coach.classList.toggle('hk-hidden', !hint);
      this.root.classList.toggle('coach', hint);
    }
    if (!v.active || v.autoVersion === this.version) return;
    this.version = v.autoVersion;
    const a = v.auto;
    this.root.classList.toggle('on', a.on);
    this.btn.classList.toggle('off-map', !a.available);
    this.btn.title = a.available ? (a.on ? 'Auto is on: your hero hunts by itself. Move or cast to take over. (Z)' : 'Auto: your hero hunts this field by itself (Z)') : 'Auto hunts in the fields, not in town';
    setText(this.status, AUTO_STATUS_TEXT[a.status]);
  }

  dispose(): void {
    this.root.remove();
  }
}
